import {useCallback, useMemo, useState} from 'react'
import {useSessionEventsQuery} from './use-session-events'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {useSessionCommandsQuery} from './useListCommands'
import {getPromptResponseResolvedPromptIds} from '../utils/permission-prompt-helpers'

export interface UseEventsPromptStateOptions {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** The session ID to fetch prompt response commands for */
  sessionId: string | undefined
  /** Whether to enable the hook */
  enabled: boolean
}

export interface EventsPromptState {
  /** The current pending prompt, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** Zero-based index of the current pending prompt. */
  pendingPromptIndex: number
  /** Total number of unresolved prompts. */
  pendingPromptCount: number
  /** Whether events are still loading */
  isLoading: boolean
  /** Not applicable for events-based prompts (always false) */
  isExpired: boolean
  /** Mark the current prompt as resolved client-side (prevents resurrection before server confirms) */
  markPromptResolved: (toolCallId: string) => void
  /** Roll back an optimistic resolution so the prompt re-appears on mutation failure */
  unmarkPromptResolved: (toolCallId: string) => void
  /** Dismiss the current prompt by marking it resolved client-side */
  dismissPrompt: () => void
  /** Select the previous unresolved prompt, if one exists */
  goToPreviousPrompt: () => void
  /** Select the next unresolved prompt, if one exists */
  goToNextPrompt: () => void
}

/**
 * Detects the earliest unresolved interactive prompt from the session's
 * conversation model and provides callbacks for optimistic resolution.
 */
export function useEventsPromptState({taskId, sessionId, enabled}: UseEventsPromptStateOptions): EventsPromptState {
  const [resolvedPromptIds, setResolvedPromptIds] = useState<Set<string>>(() => new Set())
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)

  const {data, isLoading: isEventsLoading} = useSessionEventsQuery({
    taskId,
    enabled: enabled && !!taskId,
  })
  const {data: commandsData, isLoading: isCommandsLoading} = useSessionCommandsQuery({
    sessionId,
    enabled: enabled && !!sessionId,
  })

  const model = data?.model
  const allUnresolvedPrompts = model?.allUnresolvedPrompts
  const serverResolvedIds = model?.serverResolvedIds
  const commandResolvedIds = useMemo(
    () => getPromptResponseResolvedPromptIds(commandsData?.commands, allUnresolvedPrompts),
    [commandsData?.commands, allUnresolvedPrompts],
  )

  const pendingPrompts = useMemo(() => {
    if (!allUnresolvedPrompts?.length) return []
    const unresolvedPrompts: NormalizedPrompt[] = []
    for (const prompt of allUnresolvedPrompts) {
      if (resolvedPromptIds.has(prompt.toolCallId)) continue
      if (serverResolvedIds?.has(prompt.toolCallId)) continue
      if (commandResolvedIds.has(prompt.toolCallId)) continue
      unresolvedPrompts.push(prompt)
    }
    return unresolvedPrompts
  }, [allUnresolvedPrompts, serverResolvedIds, resolvedPromptIds, commandResolvedIds])
  const isLoading = isEventsLoading || isCommandsLoading

  const selectedPromptIndex = useMemo(() => {
    if (!pendingPrompts.length) return -1
    if (!selectedPromptId) return 0
    const index = pendingPrompts.findIndex(prompt => prompt.toolCallId === selectedPromptId)
    return index === -1 ? 0 : index
  }, [pendingPrompts, selectedPromptId])

  const visiblePendingPrompt = isLoading ? null : (pendingPrompts[selectedPromptIndex] ?? null)

  const markPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => new Set(prev).add(toolCallId))
  }, [])

  const unmarkPromptResolved = useCallback((toolCallId: string) => {
    setResolvedPromptIds(prev => {
      if (!prev.has(toolCallId)) return prev
      const next = new Set(prev)
      next.delete(toolCallId)
      return next
    })
  }, [])

  const dismissPrompt = useCallback(() => {
    if (visiblePendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(visiblePendingPrompt.toolCallId))
    }
  }, [visiblePendingPrompt])

  const goToPreviousPrompt = useCallback(() => {
    setSelectedPromptId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(prompt => prompt.toolCallId === prev) : selectedPromptIndex
      const nextIndex = Math.max(0, currentIndex - 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedPromptIndex])

  const goToNextPrompt = useCallback(() => {
    setSelectedPromptId(prev => {
      const currentIndex = prev ? pendingPrompts.findIndex(prompt => prompt.toolCallId === prev) : selectedPromptIndex
      const nextIndex = Math.min(pendingPrompts.length - 1, currentIndex + 1)
      return pendingPrompts[nextIndex]?.toolCallId ?? null
    })
  }, [pendingPrompts, selectedPromptIndex])

  return {
    pendingPrompt: visiblePendingPrompt,
    pendingPromptIndex: visiblePendingPrompt ? selectedPromptIndex : -1,
    pendingPromptCount: pendingPrompts.length,
    isLoading,
    isExpired: false,
    markPromptResolved,
    unmarkPromptResolved,
    dismissPrompt,
    goToPreviousPrompt,
    goToNextPrompt,
  }
}
